import React from 'react';
import { describeApiFailure, isAccessApiError } from '@lognai/plugin-sdk';
import { Clock3 } from '@lognai/synapse-design-system-v2/icons';
import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  useToast,
} from '@lognai/synapse-design-system-v2';
import { AskLognAIIcon } from '../AskLognAIButton/AskLognAISearchBar';
import { useInsights, useDismissInsight, useSnoozeInsight } from '../../hooks/use-insights';
import { SNOOZE_DAY_OPTIONS, type SnoozeDays } from '../../api/insights';
import { DismissConfirmDialog } from './DismissConfirmDialog';
import { entityRouteFor } from '../../utils/entity-route-for';
import { SEVERITY_TONE, SEVERITY_LABEL, byTriageOrder } from '../../utils/severity';
import { relativeTime, absoluteTime } from '../../utils/relative-time';
import type { AiInsight, InsightSeverity } from '../../api/insights';

export interface LatestInsightCardProps {
  /** Navigate handler (e.g. react-router's navigate). */
  onNavigate?: (url: string) => void;
  /** Invoked when the "View all insights" link is clicked. */
  onViewAll?: () => void;
  /** Max number of insights to show stacked. */
  limit?: number;
  /** Optional presentation mode. The default preserves the dashboard card. */
  variant?: 'default' | 'workspace';
  /** Lets an embedding layout compact the card when its loaded list is empty. */
  onEmptyChange?: (empty: boolean) => void;
}

const css = `
  @property --ai-border-angle {
    syntax: '<angle>';
    inherits: false;
    initial-value: 0deg;
  }
  @keyframes ai-border-spin {
    to { --ai-border-angle: 360deg; }
  }
  .dash-insight-card {
    position: relative;
    background: var(--surface);
    border: none;
    border-radius: 8px;
    padding: 16px 20px 8px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.05);
    transition: box-shadow 0.25s ease;
  }
  [data-theme="dark"] .dash-insight-card,
  [data-theme="gray"] .dash-insight-card {
    box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2);
  }
  .dash-insight-card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    padding: 1px;
    background:
      conic-gradient(
        from var(--ai-border-angle),
        transparent 0deg,
        transparent 250deg,
        var(--primary) 300deg,
        #fff 340deg,
        transparent 360deg
      ),
      color-mix(in srgb, var(--primary) 25%, transparent);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    animation: ai-border-spin 4s linear infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .dash-insight-card::before { animation: none; }
  }

  /* ---- header ---- */
  .dash-insight-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 0 0 8px;
  }
  .dash-insight-heading {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .dash-insight-title {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    color: var(--primary);
    margin: 0;
    white-space: nowrap;
  }
  .dash-insight-counts {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .dash-insight-count {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: var(--muted-foreground);
    white-space: nowrap;
  }
  .dash-insight-count-dot {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: var(--sev-color);
    flex-shrink: 0;
  }
  .dash-insight-viewall {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-size: 14px;
    color: var(--primary);
    white-space: nowrap;
    flex-shrink: 0;
  }
  .dash-insight-viewall:hover {
    text-decoration: underline;
  }

  /* ---- severity palette: shared by the header counts and the row rails ---- */
  .dash-insight-item,
  .dash-insight-count {
    --sev-color: var(--info-default);
    --sev-wash: var(--info-subtle);
  }
  .dash-insight-item.sev-critical,
  .dash-insight-count.sev-critical {
    --sev-color: var(--destructive-default);
    --sev-wash: var(--destructive-subtle);
  }
  .dash-insight-item.sev-warning,
  .dash-insight-count.sev-warning {
    --sev-color: var(--warning-default);
    --sev-wash: var(--warning-subtle);
  }

  /* ---- rows ---- */
  .dash-insight-item {
    position: relative;
    display: grid;
    grid-template-columns: 3px minmax(0, 1fr) auto;
    align-items: stretch;
    column-gap: 12px;
    padding: 10px 8px;
    margin: 0 -8px;
    border-radius: 6px;
    transition: background-color 0.15s ease;
  }
  .dash-insight-item.clickable { cursor: pointer; }
  .dash-insight-item.clickable:hover,
  .dash-insight-item.clickable:focus-visible {
    background-color: var(--sev-wash);
  }
  .dash-insight-item.clickable:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: -2px;
  }
  .dash-insight-rail {
    border-radius: 9999px;
    background: var(--sev-color);
  }
  .dash-insight-body { min-width: 0; }
  .dash-insight-row-head {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }
  .dash-insight-row-title {
    font-size: 14px;
    font-weight: 600;
    line-height: 1.35;
    color: var(--foreground);
  }
  .dash-insight-summary {
    font-size: 13px;
    font-weight: 400;
    line-height: 19px;
    color: var(--muted-foreground);
    margin-top: 2px;
  }

  /* ---- row meta (time / dismiss / chevron) ---- */
  .dash-insight-meta {
    display: flex;
    align-items: center;
    gap: 4px;
    align-self: center;
    flex-shrink: 0;
  }
  .dash-insight-time {
    font-size: 12px;
    color: var(--muted-foreground);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }
  .dash-insight-dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 4px;
    background: none;
    color: var(--muted-foreground);
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  }
  .dash-insight-ask {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    padding: 0;
    border: none;
    border-radius: 5px;
    background: none;
    color: var(--primary);
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  .dash-insight-ask:hover,
  .dash-insight-ask[aria-expanded="true"] {
    background: var(--primary-subtle);
  }
  .dash-insight-ask:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
  .dash-insight-ask-menu {
    min-width: 220px;
  }
  .dash-insight-item:hover .dash-insight-dismiss,
  .dash-insight-item:focus-within .dash-insight-dismiss {
    opacity: 1;
  }
  .dash-insight-dismiss:hover {
    background: color-mix(in srgb, var(--muted-foreground) 15%, transparent);
    color: var(--foreground);
  }
  .dash-insight-dismiss:focus-visible {
    opacity: 1;
    outline: 2px solid var(--primary);
    outline-offset: 1px;
  }
  .dash-insight-dismiss:disabled { cursor: default; opacity: 0.4; }
  .dash-insight-chevron {
    color: var(--muted-foreground);
    flex-shrink: 0;
    transition: transform 0.15s ease, color 0.15s ease;
  }
  .dash-insight-item.clickable:hover .dash-insight-chevron {
    transform: translateX(2px);
    color: var(--sev-color);
  }
  @media (prefers-reduced-motion: reduce) {
    .dash-insight-item.clickable:hover .dash-insight-chevron { transform: none; }
  }

  .dash-insight-divider {
    height: 1px;
    background: var(--border);
    margin: 0;
  }
  .dash-insight-empty {
    font-size: 13px;
    line-height: 19px;
    color: var(--muted-foreground);
    padding: 4px 0 12px;
  }

  /* ---- loading ---- */
  @keyframes dash-insight-pulse {
    50% { opacity: 0.4; }
  }
  .dash-insight-skeleton {
    height: 10px;
    border-radius: 4px;
    background: color-mix(in srgb, var(--muted-foreground) 20%, transparent);
    animation: dash-insight-pulse 1.6s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .dash-insight-skeleton { animation: none; }
  }

  /* ---- "AI" tab ---- */
  @keyframes ai-badge-glow {
    0%   { box-shadow: 0 0 2px 1px color-mix(in srgb, var(--primary) 15%, transparent); }
    5%   { box-shadow: 0 0 10px 3px color-mix(in srgb, var(--primary) 25%, transparent), 0 0 20px 6px color-mix(in srgb, var(--primary) 10%, transparent); }
    10%, 100% { box-shadow: 0 0 2px 1px color-mix(in srgb, var(--primary) 15%, transparent); }
  }
  .dash-insight-badge {
    position: absolute;
    top: -13px;
    left: 16px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px 2px 6px;
    border-radius: 4px;
    background: var(--primary);
    border: none;
    flex-shrink: 0;
    animation: ai-badge-glow 10s ease-in-out infinite;
  }
  @media (prefers-reduced-motion: reduce) {
    .dash-insight-badge { animation: none; }
  }
  .dash-insight-badge-text {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.03em;
    color: #fff;
  }

  /* ---- Workspace operational briefing ---- */
  .workspace-briefing {
    overflow: hidden;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: var(--surface);
    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
  }
  .workspace-briefing-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 13px 18px;
    border-bottom: 1px solid var(--border);
    background: color-mix(in srgb, var(--primary) 7%, var(--surface));
  }
  .workspace-briefing-label {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--primary);
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .workspace-briefing-viewall {
    border: 0;
    padding: 4px 0;
    background: none;
    color: var(--primary);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .workspace-briefing-viewall:hover { text-decoration: underline; }
  .workspace-briefing-main {
    display: grid;
    grid-template-columns: 4px minmax(0, 1fr);
  }
  .workspace-briefing-rail { background: var(--info-default); }
  .workspace-briefing-main.sev-warning .workspace-briefing-rail { background: var(--warning-default); }
  .workspace-briefing-main.sev-critical .workspace-briefing-rail { background: var(--destructive-default); }
  .workspace-briefing-content { padding: 18px 20px 16px; min-width: 0; }
  .workspace-briefing-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }
  .workspace-briefing-title {
    margin: 0;
    color: var(--foreground);
    font-size: 18px;
    font-weight: 650;
    line-height: 1.35;
  }
  .workspace-briefing-module {
    margin-top: 5px;
    color: var(--muted-foreground);
    font-size: 12px;
    font-weight: 600;
    text-transform: capitalize;
  }
  .workspace-briefing-summary {
    max-width: 74ch;
    margin: 12px 0 0;
    color: var(--muted-foreground);
    font-size: 14px;
    line-height: 1.55;
  }
  .workspace-briefing-evidence {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
    margin-top: 16px;
  }
  .workspace-briefing-fact {
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--background);
  }
  .workspace-briefing-fact-label {
    overflow: hidden;
    color: var(--muted-foreground);
    font-size: 10px;
    font-weight: 650;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .workspace-briefing-fact-value {
    overflow: hidden;
    margin-top: 4px;
    color: var(--foreground);
    font-size: 14px;
    font-weight: 650;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .workspace-briefing-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
  }
  .workspace-briefing-action {
    display: inline-flex;
    min-height: 32px;
    align-items: center;
    justify-content: center;
    gap: 7px;
    padding: 0 12px;
    border: 1px solid var(--border);
    border-radius: 8px;
    background: var(--surface);
    color: var(--foreground);
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .workspace-briefing-action:hover { background: var(--muted); }
  .workspace-briefing-action.primary {
    border-color: var(--primary);
    background: var(--primary);
    color: var(--primary-foreground);
  }
  .workspace-briefing-dismiss { margin-left: auto; }
  .workspace-briefing-more {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1px;
    border-top: 1px solid var(--border);
    background: var(--border);
  }
  .workspace-briefing-more-item {
    min-width: 0;
    padding: 12px 18px;
    border: 0;
    background: var(--surface);
    text-align: left;
  }
  button.workspace-briefing-more-item { cursor: pointer; }
  button.workspace-briefing-more-item:hover { background: var(--muted); }
  .workspace-briefing-more-title {
    overflow: hidden;
    color: var(--foreground);
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .workspace-briefing-more-summary {
    overflow: hidden;
    margin-top: 3px;
    color: var(--muted-foreground);
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .workspace-briefing-state {
    padding: 22px 20px;
    color: var(--muted-foreground);
    font-size: 13px;
  }
  @media (max-width: 900px) {
    .workspace-briefing-evidence { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (max-width: 640px) {
    .workspace-briefing-title-row { display: block; }
    .workspace-briefing-title-row > :last-child { margin-top: 8px; }
    .workspace-briefing-evidence { grid-template-columns: 1fr; }
    .workspace-briefing-more { grid-template-columns: 1fr; }
    .workspace-briefing-dismiss { margin-left: 0; }
  }
`;

/** Returns the first sentence of a summary, including its terminating punctuation. */
const firstSentence = (text: string): string => {
  const match = text.match(/^.*?[.!?](?=\s|$)/);
  return match ? match[0] : text;
};

/** Matches entity codes like SHP-2026-00012, PO-001, SO-2026-7. */
const ENTITY_CODE = /\b[A-Z]{2,}(?:-[A-Z0-9]+)+\b/g;

/**
 * Removes any entity code that already appears in the title from the summary,
 * so the order/document name isn't repeated. Tidies up leftover whitespace.
 */
const dropRepeatedCode = (summary: string, title: string): string => {
  const codes = title.match(ENTITY_CODE);
  if (!codes) return summary;
  let result = summary;
  for (const code of codes) {
    result = result.replace(new RegExp(`\\s*\\b${code}\\b`, 'g'), '');
  }
  return result.replace(/\s{2,}/g, ' ').replace(/\s+([.,!?])/g, '$1').trim();
};

const ChevronIcon: React.FC = () => (
  <svg
    className="dash-insight-chevron"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const CloseIcon: React.FC = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

type InsightChatAction = 'explain' | 'impact' | 'actions' | 'evidence';

const INSIGHT_CHAT_ACTIONS: Array<{ id: InsightChatAction; label: string }> = [
  { id: 'explain', label: 'Explain what happened' },
  { id: 'impact', label: 'Assess business impact' },
  { id: 'actions', label: 'Recommend next actions' },
  { id: 'evidence', label: 'Review supporting evidence' },
];

const openInsightChat = (insight: AiInsight, action: InsightChatAction) => {
  const evidence = insight.evidence
    .map((fact) => `${fact.label}: ${String(fact.value)}`)
    .join('; ');
  const scope = [
    `module: ${insight.scope.pluginId}`,
    insight.scope.entityType ? `record type: ${insight.scope.entityType}` : null,
    insight.scope.entityId ? `record ID: ${insight.scope.entityId}` : null,
  ]
    .filter(Boolean)
    .join(', ');
  const context = [
    `Active LognAI insight: "${insight.title}".`,
    `Summary: ${insight.summary}`,
    `Context: ${scope}.`,
    evidence ? `Supporting evidence: ${evidence}.` : 'No structured supporting evidence was supplied.',
  ].join(' ');
  const instruction: Record<InsightChatAction, string> = {
    explain:
      'Explain what happened in clear business terms. Use current operational records and the supplied evidence, and distinguish facts from inference.',
    impact:
      'Assess the likely operational and financial impact. Quantify it only where the supplied evidence or current records support the value.',
    actions:
      'Recommend practical next actions in priority order. Tie every recommendation to the supplied evidence or current operational records.',
    evidence:
      'Review the supporting evidence, explain what each fact means, and identify any missing data or uncertainty that prevents a firm conclusion.',
  };
  const targetUrl = entityRouteFor(insight.scope);
  const pageContext =
    targetUrl && insight.scope.entityType && insight.scope.entityId
      ? {
          pluginId: insight.scope.pluginId,
          entityType: insight.scope.entityType,
          entityId: insight.scope.entityId,
          url: targetUrl,
        }
      : undefined;

  window.dispatchEvent(
    new CustomEvent('lognai:open-ai-chat', {
      detail: {
        prompt: `${instruction[action]} ${context}`,
        pageContext,
      },
    })
  );
};

const SNOOZE_LABELS: Record<SnoozeDays, string> = {
  1: 'Tomorrow',
  2: 'In 2 days',
  3: 'In 3 days',
  7: 'In a week',
};

const InsightAskMenu: React.FC<{ insight: AiInsight }> = ({ insight }) => {
  const snooze = useSnoozeInsight();
  const { toast } = useToast();

  const remindLater = (days: SnoozeDays) => {
    snooze.mutate(
      { id: insight.id, days },
      {
        onSuccess: () =>
          toast({
            title: 'Reminder set',
            // Say it is personal: dismiss is company-wide, so the difference matters.
            description: `"${insight.title}" returns to your feed ${SNOOZE_LABELS[days].toLowerCase()}.`,
          }),
        onError: () =>
          toast({
            title: 'Could not set the reminder',
            description: 'Please try again.',
            variant: 'destructive',
          }),
      }
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="dash-insight-ask"
          aria-label={`Ask LognAI about insight: ${insight.title}`}
          title="Ask LognAI"
          onClick={(event) => event.stopPropagation()}
        >
          <AskLognAIIcon size={13} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="dash-insight-ask-menu"
        onClick={(event) => event.stopPropagation()}
      >
        <DropdownMenuLabel>Ask LognAI</DropdownMenuLabel>
        {INSIGHT_CHAT_ACTIONS.map((action) => (
          <DropdownMenuItem key={action.id} onSelect={() => openInsightChat(insight, action.id)}>
            {action.label}
          </DropdownMenuItem>
        ))}
        {/* Separated from the chat actions above: these change state rather
            than opening a conversation. */}
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Remind me later</DropdownMenuLabel>
        {SNOOZE_DAY_OPTIONS.map((days) => (
          <DropdownMenuItem
            key={days}
            disabled={snooze.isPending}
            onSelect={() => remindLater(days)}
          >
            <Clock3 size={13} aria-hidden="true" />
            {SNOOZE_LABELS[days]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const InsightSlide: React.FC<{
  insight: AiInsight;
  onNavigate?: (url: string) => void;
  onDismiss: (insight: AiInsight) => void;
}> = ({ insight, onNavigate, onDismiss }) => {
  const targetUrl = entityRouteFor(insight.scope);
  const clickable = Boolean(targetUrl);
  const stamp = relativeTime(insight.computedAt);

  const handleClick = () => {
    if (targetUrl && onNavigate) onNavigate(targetUrl);
  };

  const clickHandlers = clickable
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: handleClick,
        onKeyDown: (e: React.KeyboardEvent) => {
          // Keydown bubbles up from nested controls (e.g. the Dismiss button).
          // Only handle it here when it originated on the row itself, or the
          // row's own preventDefault()/navigation would steal Space/Enter from
          // those controls' native behaviour.
          if (e.target !== e.currentTarget) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick();
          }
        },
      }
    : {};

  return (
    <div {...clickHandlers} className={`dash-insight-item sev-${insight.severity}${clickable ? ' clickable' : ''}`}>
      <span className="dash-insight-rail" aria-hidden="true" />

      <div className="dash-insight-body">
        <div className="dash-insight-row-head">
          <span className="dash-insight-row-title">{insight.title}</span>
          <Badge tone={SEVERITY_TONE[insight.severity]} variant="subtle" size="xs">
            {SEVERITY_LABEL[insight.severity]}
          </Badge>
        </div>
        <div className="dash-insight-summary">
          {dropRepeatedCode(firstSentence(insight.summary), insight.title)}
        </div>
      </div>

      <div className="dash-insight-meta">
        {stamp && (
          <time
            className="dash-insight-time"
            dateTime={insight.computedAt}
            title={absoluteTime(insight.computedAt)}
          >
            {stamp}
          </time>
        )}
        <InsightAskMenu insight={insight} />
        <button
          type="button"
          className="dash-insight-dismiss"
          aria-label={`Dismiss insight: ${insight.title}`}
          onClick={(e) => {
            e.stopPropagation();
            onDismiss(insight);
          }}
        >
          <CloseIcon />
        </button>
        {clickable && <ChevronIcon />}
      </div>
    </div>
  );
};

const MAX_VISIBLE = 5;

const SEVERITY_SUMMARY_ORDER: InsightSeverity[] = ['critical', 'warning', 'info'];

const CardShell: React.FC<{ onViewAll?: () => void; children: React.ReactNode; counts?: React.ReactNode }> = ({
  onViewAll,
  children,
  counts,
}) => (
  <>
    <style>{css}</style>
    <section className="space-y-3">
      <div className="dash-insight-card" data-testid="dashboard-latest-insight">
        <div className="dash-insight-header">
          <div className="dash-insight-heading">
            <h3 className="dash-insight-title">
              <svg width="16" height="16" viewBox="0 0 20 20" fill="var(--primary)" aria-hidden="true">
                <path d="M13,3 L14.5,9.5 L21,11 L14.5,12.5 L13,19 L11.5,12.5 L5,11 L11.5,9.5 Z" />
                <path d="M5,1 L5.9,3.6 L8.5,4.5 L5.9,5.4 L5,8 L4.1,5.4 L1.5,4.5 L4.1,3.6 Z" />
              </svg>
              Latest LognAI Insights
            </h3>
            {counts}
          </div>
          {onViewAll && (
            <button className="dash-insight-viewall" onClick={onViewAll}>
              View All
            </button>
          )}
        </div>
        {children}
      </div>
    </section>
  </>
);

export const LatestInsightCard: React.FC<LatestInsightCardProps> = ({
  onNavigate,
  onViewAll,
  limit = 20,
  variant = 'default',
  onEmptyChange,
}) => {
  const { data, isLoading, isError, error } = useInsights({ status: 'active', limit });
  // A refusal will not fix itself when "the connection recovers" — say what it is.
  const failureText = isAccessApiError(error)
    ? describeApiFailure(error).detail
    : null;
  const dismiss = useDismissInsight();
  const { toast } = useToast();
  const [pendingDismiss, setPendingDismiss] = React.useState<AiInsight | null>(null);

  // The API returns insights in generation order; an operator needs the worst
  // thing first. Sort before slicing so the top 5 are the 5 that matter.
  const all = React.useMemo(() => [...(data?.items ?? [])].sort(byTriageOrder), [data?.items]);
  const items = all.slice(0, MAX_VISIBLE);
  const empty = !isLoading && !isError && items.length === 0;
  React.useEffect(() => {
    onEmptyChange?.(empty);
  }, [empty, onEmptyChange]);

  const handleDismissConfirm = async (reason?: string) => {
    if (!pendingDismiss) return;
    try {
      await dismiss.mutateAsync({ id: pendingDismiss.id, reason });
      setPendingDismiss(null);
      toast({ title: 'Insight dismissed', description: 'Hidden for your company for 30 days.' });
    } catch (err) {
      toast({ title: 'Dismiss failed', description: (err as Error).message, tone: 'destructive' });
    }
  };

  if (variant === 'workspace') {
    const renderShell = (content: React.ReactNode) => (
      <>
        <style>{css}</style>
        <section className="workspace-briefing" data-testid="workspace-operational-briefing">
          <div className="workspace-briefing-head">
            <div className="workspace-briefing-label">
              <AskLognAIIcon size={14} aria-hidden="true" />
              Latest LognAI Insights
            </div>
            {onViewAll && (
              <button type="button" className="workspace-briefing-viewall" onClick={onViewAll}>
                View all insights
              </button>
            )}
          </div>
          {content}
        </section>
      </>
    );

    if (isLoading) {
      return renderShell(
        <div className="workspace-briefing-state">
          <div className="dash-insight-skeleton" style={{ width: '38%' }} />
          <div className="dash-insight-skeleton" style={{ width: '76%', marginTop: 12 }} />
          <div className="dash-insight-skeleton" style={{ width: '58%', marginTop: 8 }} />
        </div>
      );
    }

    if (isError) {
      return renderShell(
        <div className="workspace-briefing-state">
          {failureText ?? 'The operational briefing could not be loaded. It will reappear when the connection recovers.'}
        </div>
      );
    }

    const lead = items[0];
    if (!lead) {
      return renderShell(
        <div className="workspace-briefing-state">
          Nothing needs attention right now. New insights appear here as they are generated.
        </div>
      );
    }

    const targetUrl = entityRouteFor(lead.scope);
    const askWhy = () => {
      window.dispatchEvent(
        new CustomEvent('lognai:open-ai-chat', {
          detail: {
            prompt: `Why is this happening: "${lead.title}"? Explain the evidence and business impact from this active insight: ${lead.summary}`,
          },
        })
      );
    };

    return (
      <>
        {renderShell(
          <>
            <div className={`workspace-briefing-main sev-${lead.severity}`}>
              <span className="workspace-briefing-rail" aria-hidden="true" />
              <div className="workspace-briefing-content">
                <div className="workspace-briefing-title-row">
                  <div className="min-w-0">
                    <h2 className="workspace-briefing-title">{lead.title}</h2>
                    <div className="workspace-briefing-module">
                      {lead.scope.pluginId.replace(/[-_]/g, ' ')} · {relativeTime(lead.computedAt)}
                    </div>
                  </div>
                  <Badge tone={SEVERITY_TONE[lead.severity]} variant="subtle" size="sm">
                    {SEVERITY_LABEL[lead.severity]}
                  </Badge>
                </div>

                <p className="workspace-briefing-summary">{lead.summary}</p>

                {lead.evidence.length > 0 && (
                  <div className="workspace-briefing-evidence" aria-label="Insight evidence">
                    {lead.evidence.slice(0, 4).map((fact, index) => (
                      <div className="workspace-briefing-fact" key={`${fact.label}-${index}`}>
                        <div className="workspace-briefing-fact-label" title={fact.label}>{fact.label}</div>
                        <div className="workspace-briefing-fact-value" title={String(fact.value)}>{fact.value}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="workspace-briefing-actions">
                  <button type="button" className="workspace-briefing-action primary" onClick={askWhy}>
                    <AskLognAIIcon size={13} aria-hidden="true" />
                    Ask LognAI
                  </button>
                  {targetUrl && onNavigate && (
                    <button
                      type="button"
                      className="workspace-briefing-action"
                      onClick={() => onNavigate(targetUrl)}
                    >
                      Open affected record
                    </button>
                  )}
                  <button
                    type="button"
                    className="workspace-briefing-action workspace-briefing-dismiss"
                    onClick={() => setPendingDismiss(lead)}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            </div>

            {items.length > 1 && (
              <div className="workspace-briefing-more" aria-label="More active insights">
                {items.slice(1, 3).map((insight) => {
                  const url = entityRouteFor(insight.scope);
                  const Tag = url && onNavigate ? 'button' : 'div';
                  return (
                    <Tag
                      key={insight.id}
                      className="workspace-briefing-more-item"
                      {...(Tag === 'button'
                        ? { type: 'button', onClick: () => onNavigate?.(url as string) }
                        : {})}
                    >
                      <div className="workspace-briefing-more-title">{insight.title}</div>
                      <div className="workspace-briefing-more-summary">{firstSentence(insight.summary)}</div>
                    </Tag>
                  );
                })}
              </div>
            )}
          </>
        )}
        <DismissConfirmDialog
          open={pendingDismiss !== null}
          onOpenChange={(open) => !open && setPendingDismiss(null)}
          onConfirm={handleDismissConfirm}
          submitting={dismiss.isPending}
        />
      </>
    );
  }

  if (isLoading) {
    return (
      <CardShell>
        <div style={{ padding: '6px 0 12px' }}>
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ padding: '11px 0' }}>
              <div className="dash-insight-skeleton" style={{ width: '46%' }} />
              <div className="dash-insight-skeleton" style={{ width: '72%', marginTop: 8, height: 8 }} />
            </div>
          ))}
        </div>
      </CardShell>
    );
  }

  if (isError) {
    return (
      <CardShell onViewAll={onViewAll}>
        <div className="dash-insight-empty">
          {failureText ?? 'Insights couldn’t be loaded. They’ll reappear once the connection recovers.'}
        </div>
      </CardShell>
    );
  }

  // `data.items` is only the fetched page (limit defaults to 20) — there's no
  // `total` in InsightsListResponse. When `nextCursor` is set, more active
  // insights exist beyond this page, so each bucket's count is only a lower
  // bound: render "N+" rather than a bare "N" so it doesn't read as a fact
  // about the tenant.
  const isTruncated = Boolean(data?.nextCursor);

  const counts = SEVERITY_SUMMARY_ORDER.map((severity) => ({
    severity,
    n: all.filter((i) => i.severity === severity).length,
  })).filter((c) => c.n > 0);

  const countStrip = counts.length > 0 && (
    <span
      className="dash-insight-counts"
      title={isTruncated ? `Counts cover only the most recent ${all.length} insights` : undefined}
    >
      {counts.map(({ severity, n }) => (
        <span key={severity} className={`dash-insight-count sev-${severity}`}>
          <span className="dash-insight-count-dot" aria-hidden="true" />
          {n}
          {isTruncated ? '+' : ''} {SEVERITY_LABEL[severity].toLowerCase()}
        </span>
      ))}
    </span>
  );

  return (
    <CardShell onViewAll={onViewAll} counts={countStrip || undefined}>
      {items.length === 0 ? (
        <div className="dash-insight-empty">
          Nothing needs attention right now. New insights appear here as they’re generated.
        </div>
      ) : (
        items.map((insight, i) => (
          <React.Fragment key={insight.id}>
            {i > 0 && <div className="dash-insight-divider" />}
            <InsightSlide insight={insight} onNavigate={onNavigate} onDismiss={setPendingDismiss} />
          </React.Fragment>
        ))
      )}

      <DismissConfirmDialog
        open={pendingDismiss !== null}
        onOpenChange={(open) => !open && setPendingDismiss(null)}
        onConfirm={handleDismissConfirm}
        submitting={dismiss.isPending}
      />
    </CardShell>
  );
};
